import React from 'react';

import { link } from '@aline/routes';
import { DialogActions, DialogContent } from '@mui/material';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { AlineButton, AlineDialog, FormikAlineInput } from 'src/components';
import { useCreateReport } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

const useStyles = makeStyles()(theme => ({
  container: {
    width: 348,
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
}));

const NewReportSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
});

interface NewReportDialogProps {
  handleClose: () => void;
  open: boolean;
}

export function NewReportDialog({ handleClose, open }: NewReportDialogProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { createReport } = useCreateReport();

  return (
    <AlineDialog
      aria-labelledby="new-report-form"
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title="New Report"
      onClose={handleClose}
    >
      <Formik
        initialValues={{ name: 'Untitled report' }}
        validationSchema={NewReportSchema}
        onSubmit={async ({ name }, { setSubmitting }): Promise<void> => {
          const { data } = await createReport({
            variables: {
              input: { reportProps: { name } },
            },
          });
          setSubmitting(false);
          handleClose();
          if (data) navigate(link.ReportDetail({ reportId: data.createReport.report.id }));
        }}
      >
        {({ handleReset, handleSubmit, isSubmitting, isValid }): JSX.Element => {
          return (
            <form onReset={handleReset} onSubmit={handleSubmit}>
              <DialogContent>
                <FormikAlineInput
                  autoFocus
                  data-cy="name-report-input"
                  fieldName="name"
                  placeholder="Report name"
                />
              </DialogContent>
              <DialogActions>
                <AlineButton color="primary" variant="outlined" onClick={handleClose}>
                  Cancel
                </AlineButton>
                <AlineButton
                  className={classes.saveButton}
                  color="primary"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  Create
                </AlineButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </AlineDialog>
  );
}
